import React, { useState, useRef, useCallback } from 'react';
import { Camera, RotateCcw, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface EnhancedCameraProps {
  onPhotoCapture: (file: File) => Promise<void>;
  disabled?: boolean;
}

export const EnhancedCamera: React.FC<EnhancedCameraProps> = ({
  onPhotoCapture,
  disabled = false,
}) => {
  const [isCamera, setIsCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  const { toast } = useToast();

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: 'environment' // Use back camera by default
        },
        audio: false,
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsCamera(true);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast({
        title: "Camera Error",
        description: "Unable to access camera. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCamera(false);
  };

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw current video frame to canvas
    ctx.drawImage(video, 0, 0);
    
    // Convert to blob and create URL
    canvas.toBlob((blob) => {
      if (blob) {
        const photoUrl = URL.createObjectURL(blob);
        setCapturedPhoto(photoUrl);
        stopCamera();
      }
    }, 'image/jpeg', 0.9);
  }, []);

  const retakePhoto = () => {
    if (capturedPhoto) {
      URL.revokeObjectURL(capturedPhoto);
      setCapturedPhoto(null);
    }
    startCamera();
  };

  const handleUpload = async () => {
    if (!capturedPhoto || !canvasRef.current) return;

    setUploading(true);
    try {
      // Convert canvas to blob
      canvasRef.current.toBlob(async (blob) => {
        if (!blob) return;
        
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        await onPhotoCapture(file);
        
        // Clean up
        URL.revokeObjectURL(capturedPhoto);
        setCapturedPhoto(null);
        
        toast({
          title: "Success",
          description: "Photo uploaded successfully!",
        });
        
        setUploading(false);
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: "Upload Error",
        description: "Failed to upload photo.",
        variant: "destructive",
      });
      setUploading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const photoUrl = URL.createObjectURL(file);
      setCapturedPhoto(photoUrl);
      
      // Load image to canvas for consistency
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
      };
      img.src = photoUrl;
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
        {capturedPhoto ? (
          <img
            src={capturedPhoto}
            alt="Captured"
            className="w-full h-full object-cover"
          />
        ) : isCamera ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <Camera className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Camera preview</p>
            </div>
          </div>
        )}
        
        {isCamera && (
          <Button
            onClick={stopCamera}
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      <div className="flex flex-col gap-1">
        {!capturedPhoto ? (
          <>
            {!isCamera ? (
              <>
                <Button
                  onClick={startCamera}
                  disabled={disabled}
                  variant="default"
                  size="sm"
                  className="w-full text-xs"
                >
                  <Camera className="w-3 h-3 mr-1" />
                  Camera
                </Button>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={disabled}
                  />
                  <Button
                    disabled={disabled}
                    variant="outline"
                    size="sm"
                    className="w-full text-xs"
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Upload
                  </Button>
                </div>
              </>
            ) : (
              <Button
                onClick={capturePhoto}
                disabled={disabled}
                variant="default"
                size="sm"
                className="w-full"
              >
                <Camera className="w-3 h-3 mr-1" />
                Capture
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              onClick={handleUpload}
              disabled={disabled || uploading}
              variant="default"
              size="sm"
              className="w-full text-xs"
            >
              {uploading ? (
                <>
                  <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></span>
                  Upload
                </>
              ) : (
                <>
                  <Upload className="w-3 h-3 mr-1" />
                  Upload
                </>
              )}
            </Button>
            <Button
              onClick={retakePhoto}
              disabled={disabled || uploading}
              variant="outline"
              size="sm"
              className="w-full text-xs"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Retake
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default EnhancedCamera;